import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import Editor from '@components/editor/Editor';
import LoaderComponent from '@components/LoaderComponent';
import { toastError } from '@components/Toast';

import { SelectedFileViewComponent } from '@features/task/components/TaskManageComponents/SelectedFileViewComponent';

import { fetchDesignToolComment } from '@store/actions/DesignToolAction';

import ApiClient from '@lib/apiClient';

import { getDefaultMention, getUserInfo } from '@utils/Util';

const POST_TAG = {
	PRODUCT_QUOTE_COMMENT: 'Costing',
	PRODUCT_SAMPLE_COMMENT: 'Sample',
	PRODUCT_DEVELOPMENT_COMMENT: 'Design'
};

const CommentReplyPanel = ({ parentPost, productId, selectedPostTag, generateParams }) => {
	const designToolCommentStore = useSelector((store) => store.designToolCommentStore);
	const [openReply, setOpenReply] = useState(false);
	const [loader, setLoader] = useState(false);
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [commentError, setCommentError] = useState('');
	const { id } = useParams();
	const [editorContent, setEditorContent] = useState(null);
	const [mentionIds, setMentionIds] = useState([]);
	const [defaultMention, setDefaultMention] = useState(null);
	const dispatch = useDispatch();

	useEffect(() => {
		const { postedBy } = parentPost;
		if (Object.keys(postedBy).length > 0) {
			setDefaultMention(getDefaultMention(postedBy.name, postedBy.id));
		}
	}, []);

	const handleReply = () => {
		let newComment = editorContent === null ? '' : editorContent.toString().replace('<p></p>', '').trim();
		if (!newComment) {
			setCommentError('Comment required');
			return;
		}
		setCommentError('');
		setLoader(true);
		let body = {
			documentDTOList: selectedFiles,
			postId: parentPost.id,
			postPositionNo: parentPost.postPositionNo,
			productId: productId || id,
			postType: 'PRODUCT_DEVELOPMENT_COMMENT',
			text: editorContent,
			taggedUserIdList: mentionIds
		};
		if (
			['PRODUCT_SAMPLE_COMMENT', 'PRODUCT_QUOTE_COMMENT', 'PRODUCT_DEVELOPMENT_COMMENT'].includes(
				selectedPostTag
			)
		) {
			body.postTag = selectedPostTag;
		}
		ApiClient.POST('addPostReply', body)
			.then(() => {
				dispatch(fetchDesignToolComment(productId || id, generateParams(0)));
				setLoader(false);
				setOpenReply(false);
			})
			.catch((error) => {
				setLoader(false);
				toastError(error.response.data.message);
			});
	};

	const onMultipleFileSelect = (e, docType) => {
		const files = Array.from(e.target.files);
		const fileList = [...selectedFiles];
		files.map((item) => {
			const data = {
				name: item.name,
				docMimeType: item.type,
				documentType: docType,
				print: false
			};
			const reader = new FileReader();
			reader.readAsDataURL(item);
			reader.onload = async () => {
				data.base64Str = reader.result;
				fileList.push(data);
			};
		});

		setTimeout(() => {
			setSelectedFiles(fileList);
		}, 500);
	};

	const removeFile = (index) => {
		const fileList = selectedFiles.filter((file, i) => i !== index);
		setSelectedFiles(fileList);
	};

	const renderFilePreview = () => {
		if (selectedFiles.length > 0) {
			return (
				<div className={'files-n-photos custom-scrollbar}'}>
					{selectedFiles.map((file, i) => {
						return (
							<SelectedFileViewComponent
								showRemoveOption={true}
								file={file}
								key={i}
								index={i}
								remove={removeFile}
							/>
						);
					})}
				</div>
			);
		}
	};

	const handleCancel = (event) => {
		event.stopPropagation();
		setSelectedFiles([]);
		setOpenReply(false);
	};

	const renderCommentBox = () => {
		return (
			<div className="comments-container expand">
				<div className="artboard-comment-box techpack-editor">
					{getUserInfo()?.profilePicDocument?.docUrl && (
						<img
							src={getUserInfo()?.profilePicDocument?.docUrl}
							alt="profile"
							className="profile"
						/>
					)}
					{!getUserInfo()?.profilePicDocument?.docUrl && (
						<img
							src={`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`}
							alt="profile"
							className="profile"
						/>
					)}
					<Editor
						setEditorContent={setEditorContent}
						setMentionIds={setMentionIds}
						defaultMention={defaultMention}
						memberList={designToolCommentStore?.memberList?.map((user) => {
							return {
								memberId: user.memberId,
								value: user.value
							};
						})}
					/>

					{commentError && <p className="error">{commentError}</p>}
				</div>
				{renderFilePreview()}
				<div className="artboard-comment-footer">
					<span className="upload">
						<label htmlFor="attachment">
							<img src={`${process.env.PUBLIC_URL}/icons/attach20.svg`} alt="attachment" />
						</label>
						<input
							type="file"
							id="attachment"
							name="attachment"
							multiple
							onChange={(e) => onMultipleFileSelect(e, 'ACCESSORIES_DESIGN')}
						/>
					</span>
					<span className="action-buttons">
						<button onClick={(event) => handleCancel(event)}>CANCEL</button>
						<button onClick={handleReply}>POST</button>
					</span>
				</div>
			</div>
		);
	};

	return (
		<LoaderComponent loading={loader}>
			{!openReply && (
				<>
					<button
						onClick={() => {
							setOpenReply(true);
						}}
						className="button text reply"
					>
						REPLY
					</button>
					{!selectedPostTag && (
						<span className={`ml-1 status ${POST_TAG[parentPost?.postTag]}`}>
							{POST_TAG[parentPost?.postTag]}
						</span>
					)}
				</>
			)}

			{openReply && renderCommentBox()}
		</LoaderComponent>
	);
};

export default CommentReplyPanel;
